// components/AccessDashboard.js - ENHANCED with complete cancellation support - PRESERVES ALL ORIGINAL CODE
// Add React import for useCallback
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Download, Database, AlertTriangle, BarChart3, Table, LogOut, Search, X, ListChecks } from 'lucide-react';
import { styles } from '../styles';
import { utils } from '../utils';
import { useDashboard } from '../hooks/useDashboard';
import DashboardGrid from './DashboardGrid';
import DataTable from './DataTable';
import FiltersSection from './FiltersSection';
import CountrySummary from './CountrySummary';
import SidCheck from './SidCheck';
import AccessListChanges from './AccessListChanges';
import AccessListHistory from './AccessListHistory';
import DailyRanges from './DailyRanges';
import { getIngestionStatus, triggerIngestion } from '../services/api/ingestionApi';
import { cancelAnalysis, getAnalysisStatus, getTableSize, getTopDestinations } from '../services/api/dashboardApi';

const AccessDashboard = ({ onLogout }) => {
    // Updated to support four view modes: 'table', 'summary', 'sidcheck', 'accesslist'
    const [viewMode, setViewMode] = useState('dailyranges');
    const [tableSize, setTableSize] = useState({ size: 0, unit: 'MB' });
    const [topDestinations, setTopDestinations] = useState([]);
    const [freshDataRunning, setFreshDataRunning] = useState(false);
    const [freshDataStatus, setFreshDataStatus] = useState('Idle');
    const [dashboardRefreshToken, setDashboardRefreshToken] = useState(0);

    // 🚨 NEW: Enhanced operation tracking state
    const [activeOperations, setActiveOperations] = useState({
        python: false,
        database: 0,
        total: 0,
        cancelling: false
    });

    const {
        accessData, loading, error, searchTerm, originFilter, countryFilter,
        connectionStatus, tables, selectedTable, countries, origins, stats, pagination,
        initializeDashboard, handleSearchChange, handleOriginFilterChange,
        handleCountryFilterChange, handleRefresh, resetFilters, loadMoreData, setSelectedTable
    } = useDashboard();

    // 🚨 ENHANCED: Cancel ALL Operations Function (Python + Database)
    const handleCancelPythonAnalysis = async () => {
        try {
            console.log('🛑 Cancelling ALL operations (Python + Database)...');

            // Update UI immediately to show cancellation is in progress
            setActiveOperations(prev => ({ ...prev, cancelling: true }));

            const result = await cancelAnalysis();

            if (result.success) {
                console.log('✅ All operations cancelled:', result.message);
                console.log('✅ Cancelled items:', result.cancelled_items);

                // Reset active operations state
                setActiveOperations({
                    python: false,
                    database: 0,
                    total: 0,
                    cancelling: false
                });
            } else {
                console.error('❌ Failed to cancel operations:', result.error);
                setActiveOperations(prev => ({ ...prev, cancelling: false }));
            }
        } catch (error) {
            console.error('❌ Error cancelling operations:', error);
            setActiveOperations(prev => ({ ...prev, cancelling: false }));
        }
    };

    const fetchScriptStatus = useCallback(async () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            return;
        }

        try {
            const result = await getIngestionStatus();
            setFreshDataRunning(Boolean(result?.state?.is_running));
            const statusText = result?.state?.is_running
                ? 'running'
                : (result?.service_last_status || 'Idle');
            setFreshDataStatus(statusText);
        } catch (error) {
            setFreshDataStatus('Unavailable');
        }
    }, []);

    const handleGetFreshData = async () => {
        if (freshDataRunning) return;
        try {
            setFreshDataRunning(true);
            setFreshDataStatus('Running');
            await triggerIngestion('frontend-access-dashboard');
            setFreshDataStatus('Success');
            setDashboardRefreshToken(prev => prev + 1);
            handleRefresh();
        } catch (error) {
            setFreshDataStatus('Failed');
        } finally {
            setFreshDataRunning(false);
        }
    };

    // 🚨 NEW: Monitor active operations status
    useEffect(() => {
        const checkOperationsStatus = async () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }

            try {
                const status = await getAnalysisStatus();
                    const nextState = {
                        python: status.python_running || false,
                        database: (status.active_db_requests || 0),
                        total: status.total_active || 0,
                        cancelling: false
                    };

                    setActiveOperations((prev) => {
                        if (
                            prev.python === nextState.python &&
                            prev.database === nextState.database &&
                            prev.total === nextState.total &&
                            prev.cancelling === nextState.cancelling
                        ) {
                            return prev;
                        }
                        return nextState;
                    });
            } catch (error) {
                // Silently handle - backend might not have this endpoint yet
                console.debug('Could not fetch operations status:', error);
            }
        };

        // Check status immediately
        checkOperationsStatus();

        // Poll every 10 seconds
        const interval = setInterval(checkOperationsStatus, 10000);

        return () => clearInterval(interval);
    }, []);

    // Calculate additional stats
    const fetchTableStats = useCallback(async () => {
        if (!selectedTable) return;

        try {
            const sizeData = await getTableSize(selectedTable);
            setTableSize({ size: sizeData.size, unit: sizeData.unit });

            const topData = await getTopDestinations(selectedTable, 5);
            setTopDestinations(topData.destinations || []);
        } catch (err) {
            console.error('Failed to fetch additional stats:', err);
        }
    }, [selectedTable]);

    // Remove unused setViewModeDirectly function since we use setViewMode directly

    useEffect(() => {
        initializeDashboard();
    }, [initializeDashboard]);

    useEffect(() => {
        if (selectedTable && connectionStatus === 'connected') {
            fetchTableStats();
        }
    }, [selectedTable, connectionStatus, fetchTableStats]);

    useEffect(() => {
        fetchScriptStatus();
        const interval = setInterval(fetchScriptStatus, 15000);
        return () => clearInterval(interval);
    }, [fetchScriptStatus]);

    // Since backend handles filtering, accessData is already filtered
    // No need for client-side filtering when backend is doing it
    const hasActiveFilters = utils.hasActiveFilters(searchTerm, originFilter, countryFilter);
    const uniqueDestinations = new Set(accessData.map(item => item.access_destination)).size;

    // 🚨 NEW: Determine if cancel button should be shown
    const shouldShowCancelButton = activeOperations.total > 0 || loading;

    const containerStyle = {
        width: '100%',
        padding: '20px',
        margin: '0'
    };

    return (
        <div style={styles.container}>
            <div style={containerStyle}>
                {/* Header */}
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>
                            <Database size={40} style={{ color: '#8338ec' }} />
                            Access Monitoring Dashboard
                        </h1>
                        <p style={styles.subtitle}>Real-time security access monitoring and analytics</p>
                    </div>

                    <div style={styles.headerControls}>
                        <div style={utils.getConnectionStatusStyle(connectionStatus)}>
                            <div style={{
                                ...styles.statusDot,
                                backgroundColor: utils.getStatusDotColor(connectionStatus)
                            }}></div>
                            <span>
                                {connectionStatus === 'connected' ? 'Connected' :
                                    connectionStatus === 'disconnected' ? 'Offline' : 'Checking...'}
                            </span>
                        </div>

                        {/* Refresh Button - Blue */}
                        <button onClick={handleRefresh} disabled={loading}
                                style={{
                                    ...styles.button,
                                    backgroundColor: loading ? '#6b7280' : '#3a86ff',
                                    color: 'white',
                                    borderColor: loading ? '#6b7280' : '#3a86ff',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                                onMouseOver={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#1d4ed8';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (!loading) {
                                        e.target.style.backgroundColor = '#3a86ff';
                                    }
                                }}>
                            <RefreshCw size={16} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                            Refresh
                        </button>

                        {/* Get Fresh Data Button */}
                        <button
                            onClick={handleGetFreshData}
                            disabled={freshDataRunning}
                            style={{
                                ...styles.button,
                                backgroundColor: freshDataRunning ? '#16a34a' : '#22c55e',
                                color: '#0f172a',
                                borderColor: freshDataRunning ? '#16a34a' : '#22c55e',
                                cursor: freshDataRunning ? 'not-allowed' : 'pointer',
                                animation: freshDataRunning ? 'freshDataFlash 1s infinite' : 'none'
                            }}
                            onMouseOver={(e) => {
                                if (!freshDataRunning) e.target.style.backgroundColor = '#16a34a';
                            }}
                            onMouseOut={(e) => {
                                if (!freshDataRunning) e.target.style.backgroundColor = '#22c55e';
                            }}
                            title={`Get fresh data (${freshDataStatus})`}
                        >
                            <RefreshCw size={16} style={freshDataRunning ? { animation: 'spin 1s linear infinite' } : {}} />
                            Get_fresh_data
                        </button>

                        {/* 🚨 ENHANCED: Cancel All Operations Button - Only show when needed */}
                        {shouldShowCancelButton && (
                            <button
                                onClick={handleCancelPythonAnalysis}
                                disabled={activeOperations.cancelling}
                                style={{
                                    ...styles.button,
                                    backgroundColor: activeOperations.cancelling ? '#6b7280' : '#ef4444',
                                    color: '#ffffff',
                                    borderColor: activeOperations.cancelling ? '#6b7280' : '#ef4444'
                                }}
                                onMouseOver={(e) => {
                                    if (!activeOperations.cancelling) e.target.style.backgroundColor = '#dc2626';
                                }}
                                onMouseOut={(e) => {
                                    if (!activeOperations.cancelling) e.target.style.backgroundColor = '#ef4444';
                                }}
                                title={`Cancel all operations${activeOperations.python ? ' (Python analysis)' : ''}${activeOperations.python && activeOperations.database > 0 ? ' + ' : ''}${activeOperations.database > 0 ? `${activeOperations.database} database requests` : ''}`}
                            >
                                <X size={16} style={activeOperations.cancelling ? { animation: 'spin 1s linear infinite' } : {}} />
                                {activeOperations.cancelling ? 'Cancelling...' :
                                    activeOperations.total > 0 ? `Cancel All (${activeOperations.total})` :
                                        'Cancel Analysis'}
                            </button>
                        )}

                        {/* Export CSV Button - Orange */}
                        <button onClick={() => utils.exportToCSV(accessData)}
                                style={{
                                    ...styles.button,
                                    backgroundColor: '#fb5607',
                                    color: 'white',
                                    borderColor: '#fb5607'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#ea580c'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#fb5607'}>
                            <Download size={16} />
                            Export CSV
                        </button>

                        {/* Detailed View Button - Purple */}
                        <button
                            onClick={() => setViewMode('table')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'table' ? '#8338ec' : '#64748b',
                                color: 'white',
                                borderColor: viewMode === 'table' ? '#8338ec' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'table') {
                                    e.target.style.backgroundColor = '#7c3aed';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'table') {
                                    e.target.style.backgroundColor = '#64748b';
                                }
                            }}>
                            <Table size={16} />
                            Detailed View
                        </button>

                        {/* Country Summary Button - Pink */}
                        <button
                            onClick={() => setViewMode('summary')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'summary' ? '#ff006e' : '#64748b',
                                color: 'white',
                                borderColor: viewMode === 'summary' ? '#ff006e' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'summary') {
                                    e.target.style.backgroundColor = '#e11d48';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'summary') {
                                    e.target.style.backgroundColor = '#64748b';
                                }
                            }}>
                            <BarChart3 size={16} />
                            Country Summary
                        </button>

                        {/* Sid Check Button - Yellow */}
                        <button
                            onClick={() => setViewMode('sidcheck')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'sidcheck' ? '#ffbe0b' : '#64748b',
                                color: viewMode === 'sidcheck' ? '#000' : 'white',
                                borderColor: viewMode === 'sidcheck' ? '#ffbe0b' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'sidcheck') {
                                    e.target.style.backgroundColor = '#f59e0b';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'sidcheck') {
                                    e.target.style.backgroundColor = '#64748b';
                                }
                            }}>
                            <Search size={16} />
                            Sid Check
                        </button>

                        {/* Access List Changes Button - Green */}
                        <button
                            onClick={() => setViewMode('accesslist')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'accesslist' ? '#22c55e' : '#64748b',
                                color: 'white',
                                borderColor: viewMode === 'accesslist' ? '#22c55e' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'accesslist') {
                                    e.target.style.backgroundColor = '#16a34a';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'accesslist') {
                                    e.target.style.backgroundColor = '#64748b';
                                }
                            }}
                        >
                            <ListChecks size={16} />
                            Access List Changes
                        </button>

                        {/* Access List History Button - Blue */}
                        <button
                            onClick={() => setViewMode('accesslisthistory')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'accesslisthistory' ? '#38bdf8' : '#64748b',
                                color: '#0f172a',
                                borderColor: viewMode === 'accesslisthistory' ? '#38bdf8' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'accesslisthistory') {
                                    e.target.style.backgroundColor = '#0ea5e9';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'accesslisthistory') {
                                    e.target.style.backgroundColor = '#64748b';
                                }
                            }}
                        >
                            <ListChecks size={16} />
                            Access List History
                        </button>

                        <button
                            onClick={() => setViewMode('dailyranges')}
                            style={{
                                ...styles.button,
                                backgroundColor: viewMode === 'dailyranges' ? '#f59e0b' : '#64748b',
                                color: '#0f172a',
                                borderColor: viewMode === 'dailyranges' ? '#f59e0b' : '#64748b'
                            }}
                            onMouseOver={(e) => {
                                if (viewMode !== 'dailyranges') e.target.style.backgroundColor = '#d97706';
                            }}
                            onMouseOut={(e) => {
                                if (viewMode !== 'dailyranges') e.target.style.backgroundColor = '#64748b';
                            }}
                        >
                            <ListChecks size={16} />
                            Daily Ranges
                        </button>

                        {/* Logout Button - Red (keeping red for logout as it's conventional) */}
                        {onLogout && (
                            <button
                                onClick={onLogout}
                                style={{
                                    ...styles.button,
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    borderColor: '#dc2626'
                                }}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={styles.errorAlert}>
                        <AlertTriangle size={20} />
                        {error}
                    </div>
                )}

                {/* 🚨 NEW: Operations Status Indicator */}
                {activeOperations.total > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        backgroundColor: 'rgba(251, 191, 36, 0.1)',
                        border: '1px solid rgba(251, 191, 36, 0.3)',
                        borderRadius: '8px',
                        fontSize: '0.875rem',
                        color: '#fbbf24'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#fbbf24',
                            marginRight: '12px',
                            animation: 'pulse 2s infinite'
                        }} />
                        <span>
                            {activeOperations.python && `Python analysis running`}
                            {activeOperations.python && activeOperations.database > 0 && ` • `}
                            {activeOperations.database > 0 && `${activeOperations.database} database requests`}
                            {` • Total: ${activeOperations.total} active operations`}
                        </span>
                    </div>
                )}

                {/* Dashboard Grid */}
                <DashboardGrid
                    stats={stats}
                    tableSize={tableSize}
                    uniqueDestinations={uniqueDestinations}
                    topDestinations={topDestinations}
                    selectedTable={selectedTable}
                    refreshToken={dashboardRefreshToken}
                />

                {/* Filters and Search - Show for all views except Sid Check and Access List views */}
                {viewMode !== 'sidcheck' && viewMode !== 'accesslist' && viewMode !== 'accesslisthistory' && viewMode !== 'dailyranges' && (
                    <FiltersSection
                        hasActiveFilters={hasActiveFilters}
                        filteredData={accessData} // Backend-filtered data
                        accessData={accessData}
                        resetFilters={resetFilters}
                        searchTerm={searchTerm}
                        handleSearchChange={handleSearchChange}
                        selectedTable={selectedTable}
                        setSelectedTable={setSelectedTable}
                        tables={tables}
                        originFilter={originFilter}
                        handleOriginFilterChange={handleOriginFilterChange}
                        origins={origins}
                        countryFilter={countryFilter}
                        handleCountryFilterChange={handleCountryFilterChange}
                        countries={countries}
                    />
                )}

                {/* View Content - Three different views */}
                {viewMode === 'table' && (
                    <DataTable
                        loading={loading}
                        filteredData={accessData}
                        searchTerm={searchTerm}
                        connectionStatus={connectionStatus}
                        hasActiveFilters={hasActiveFilters}
                        resetFilters={resetFilters}
                        pagination={pagination}
                        loadMoreData={loadMoreData}
                    />
                )}

                {viewMode === 'summary' && (
                    <CountrySummary
                        searchTerm={searchTerm}
                        originFilter={originFilter}
                        countryFilter={countryFilter}
                        selectedTable={selectedTable}
                    />
                )}

                {viewMode === 'sidcheck' && (
                    <SidCheck
                        searchTerm={searchTerm}
                        originFilter={originFilter}
                        countryFilter={countryFilter}
                        selectedTable={selectedTable}
                        handleSearchChange={handleSearchChange}
                        handleOriginFilterChange={handleOriginFilterChange}
                        handleCountryFilterChange={handleCountryFilterChange}
                        countries={countries}
                        origins={origins}
                    />
                )}

                {viewMode === 'accesslist' && (
                    <AccessListChanges />
                )}

                {viewMode === 'accesslisthistory' && (
                    <AccessListHistory />
                )}

                {viewMode === 'dailyranges' && (
                    <DailyRanges />
                )}

                {/* 🚨 NEW: Pulse animation for operation indicators */}
                <style>
                    {`
                        @keyframes pulse {
                            0%, 100% {
                                opacity: 1;
                            }
                            50% {
                                opacity: 0.5;
                            }
                        }
                        @keyframes freshDataFlash {
                            0%, 100% {
                                box-shadow: 0 0 0 rgba(16, 185, 129, 0);
                                transform: translateY(0);
                            }
                            50% {
                                box-shadow: 0 0 12px rgba(16, 185, 129, 0.8);
                                transform: translateY(-1px);
                            }
                        }
                    `}
                </style>
            </div>
        </div>
    );
};

export default AccessDashboard;
